import { WithCleanup } from "../async/WithCleanup"

type WaitQueue = Array<() => void>

/**
 * Manages mutual exclusion locks for tasks identified by generic keys.
 * Ensures only one task with the same ID executes at a time, queuing others.
 *
 * @example
 * ```ts
 * import { LockManager } from "@pdfking/tfunc"
 *
 * const locks = new LockManager<string>()
 *
 * // Manually acquire and release
 * const release = await locks.acquire("resource-1")
 * try {
 *   // Critical section
 * } finally {
 *   release()
 * }
 *
 * // Or use schedule for automatic cleanup
 * const result = await locks.schedule("resource-1", async () => {
 *   return await fetchData()
 * })
 *
 * // For async generators
 * for await (const item of locks.schedule_iterable("resource-1", stream)) {
 *   console.log(item)
 * }
 * ```
 */
export class LockManager<T> implements LockManager<T> {
  private locks = new Map<T, WaitQueue>()
  /**
   * Acquires a lock for the given task ID.
   * Returns immediately if no lock exists, or waits until the lock is released.
   *
   * @param task_id - Unique identifier for the task/resource
   * @returns Promise resolving to a release function
   */
  async acquire(task_id: T): Promise<() => void> {
    return new Promise<() => void>(resolve => {
      // Get or create lock state
      let queue = this.locks.get(task_id)
      if (queue == undefined) {
        queue = []
        this.locks.set(task_id, queue)
        resolve(this.create_release_function(task_id, queue))
        return
      }

      // Lock is busy, add to wait queue
      queue.push(() => resolve(this.create_release_function(task_id, queue)))
    })
  }
  private create_release_function(task_id: T, queue: WaitQueue): () => void {
    return () => {
      const next = queue.shift()
      if (next) {
        next()
      } else {
        // Optional: Clean up empty lock states to prevent memory leaks
        this.locks.delete(task_id)
      }
    }
  }

  /**
   * Schedules a function to run with automatic lock acquisition and release.
   *
   * @param task_id - Unique identifier for the task/resource
   * @param func - Async function to execute while holding the lock
   * @returns Promise resolving to the function's return value
   */
  async schedule<G>(task_id: T, func: () => Promise<G>): Promise<G> {
    const release = await this.acquire(task_id)
    return WithCleanup(func(), release)
  }

  /**
   * Schedules an async generator to run with automatic lock acquisition and release.
   *
   * @param task_id - Unique identifier for the task/resource
   * @param stream - Async generator to iterate while holding the lock
   * @returns AsyncGenerator yielding the same items as the input stream
   */
  async *schedule_iterable<G>(
    task_id: T,
    stream: AsyncGenerator<G>
  ): AsyncGenerator<G> {
    const release = await this.acquire(task_id)
    try {
      yield* stream
    } finally {
      release()
    }
  }
}
